import { memo, useState } from 'react';
import { useQuery } from 'react-query';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import CurrencyFormat from 'components/ui/text-formats/CurrencyFormat';
// import Widget from './Doughnut';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import LightBox from 'components/ui/dialogs/LightBox';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import SalaryForStaff from './SalaryForStaff';

export default memo(function Salary() {
  const axiosPrivate = useAxiosPrivate();
  const { search } = useLocation();
  const { t } = useTranslation();
  const [openImageModal, setOpenImageModal] = useState(false);
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [itemData, setItemData] = useState({});

  const { data, isLoading } = useQuery({
    queryKey: ['/hr/alluser', search],
    queryFn: async function () {
      const response = await axiosPrivate.get(`/hr/alluser${search}`);
      return response.data.data;
    },
    enabled: !hasError,
    onError: (error) => {
      setHasError(true);
    },
    retry: false,
  });

  return (
    <div className='h-full relative'>
      {isLoading && (
        <div className='flex items-center justify-center absolute inset-0 z-10 bg-[rgba(255,255,255,0.8)] mb-2'>
          <CircularProgress />
        </div>
      )}

      <div className='h-full mb-8'>
        {/* <div className='text-lg font-semibold mb-2 mt-4'>{office}</div> */}
        <div className='h-[calc(100%-28px)] flex flex-col gap-3 pt-2'>
          <div className='h-fit w-full grid grid-cols-4 gap-2 bg-white p-4 rounded-lg'>
            {/* <Widget
              data={{ ...data, date: data?.users?.[0]?.report?.month }}
              stats={data?.global}
            /> */}
            {data?.global
              ? Object?.entries?.(data?.global)?.map(([name, value]) => (
                  <div key={name} className='max-h-20'>
                    <div className='bg-slate-50 p-4'>
                      <div className='font-bold text-gray-500'>
                        {t(`boss.offices.workStatuses.${name}`)}
                      </div>
                      <div className='text-[20px] font-bold'>
                        {value ? Number(Number(value).toFixed(2)) : 0}{' '}
                        {t('common.fields.mins')}
                      </div>
                    </div>
                  </div>
                ))
              : ''}
          </div>
          <div className='col-span-2 flex-grow'>
            <TableContainer className='!base-border !shadow-none !h-full'>
              <Table className='!bg-white' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell className='!bg-gray-50'>#</TableCell>
                    <TableCell className='!bg-gray-50'>
                      {t('common.fields.name')}
                    </TableCell>
                    <TableCell className='!bg-gray-50'>
                      {t('common.fields.salary')}
                    </TableCell>
                    <TableCell className='!bg-gray-50'>
                      {t('common.table.month')}
                    </TableCell>
                    <TableCell className='!bg-gray-50'>
                      {t('boss.offices.percentOfWork')}
                    </TableCell>
                    <TableCell className='!bg-gray-50'>
                      {t('common.table.producedOutput')}
                    </TableCell>
                    <TableCell className='!bg-gray-50'>
                      {t('common.table.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody className='overflow-hidden'>
                  {data?.users?.map((row, index) => {
                    return (
                      <TableRow key={index} hover className='cursor-pointer'>
                        <TableCell size='small'>
                          <div className='min-h-[28px] h-full flex items-center'>
                            {row?.report?.image ? (
                              <img
                                src={`${row?.report?.image}`}
                                className='w-7 h-7 object-cover  transition-all rounded-full cursor-pointer hover:brightness-50'
                                alt=''
                                onClick={() => {
                                  setOpenImageModal(true);
                                  setItemData({
                                    image: row?.report?.image,
                                    name: row?.user_name,
                                  });
                                }}
                              />
                            ) : (
                              <div>
                                <i className='bi bi-no-image' />
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell size='small'>{row?.user_name}</TableCell>
                        <TableCell size='small'>
                          <CurrencyFormat
                            value={row?.report?.salary}
                            suffix={` ${row?.report?.valute}`}
                          />
                        </TableCell>
                        <TableCell size='small'>
                          {moment(row?.report?.month, 'YYYY-MM').format(
                            'MMMM YYYY'
                          )}
                        </TableCell>
                        <TableCell size='small'>
                          {(
                            (row?.report?.stats?.total_minutes /
                              row?.report?.stats?.planned_minutes) *
                            100
                          ).toFixed(2) || 0}
                          %
                        </TableCell>
                        <TableCell size='small'>
                          <CurrencyFormat
                            value={Number(
                              (
                                (row?.report?.stats?.total_minutes /
                                  row?.report?.stats?.planned_minutes) *
                                row?.report?.salary
                              ).toFixed(2) || 0
                            )}
                            suffix={` ${row?.report?.valute}`}
                          />
                        </TableCell>
                        <TableCell size='small'>
                          <Button
                            size='small'
                            color='info'
                            variant='contained'
                            className='!max-w-[45px]'
                            onClick={() => {
                              setOpenInfoModal(true);
                              setItemData(row);
                            }}
                          >
                            <i className='bi bi-pie-chart' />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>
      </div>
      {openImageModal && itemData?.image ? (
        // yet-another-react-lightbox
        <LightBox
          isOpen={openImageModal}
          style={{ zIndex: 1040 }}
          captions={[`${itemData?.name}`]}
          titles={[`${itemData?.name}`]}
          images={[`${itemData?.image}`]}
          photoIndex={0}
          setIsOpen={() => {
            setOpenImageModal(false);
            setItemData({});
          }}
        />
      ) : null}
      {openInfoModal && (
        <SalaryForStaff
          open={openInfoModal}
          handleClose={() => {
            setOpenInfoModal(false);
            setItemData({});
          }}
          data={itemData}
        />
      )}
    </div>
  );
});
